import { Extension, type Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { summaryStreamTransaction } from "../utils/summaryEditorReview";

type GenerationState = { active: boolean; decorations: DecorationSet };
const generationKey = new PluginKey<GenerationState>("summaryGeneration");

function trailingWords(doc: Node): DecorationSet {
    let position: number | undefined;
    let lastText = "";
    doc.descendants((node, pos) => {
        // Skip the empty paragraph Tiptap adds after headings, tables and lists.
        if (node.isTextblock && node.content.size) {
            position = pos + 1 + node.content.size;
            lastText = node.textContent;
        }
    });
    // Even an empty report has a paragraph where the first words will arrive.
    if (position === undefined) {
        doc.descendants((node, pos) => {
            if (position === undefined && node.isTextblock) position = pos + 1;
        });
    }
    if (position === undefined) return DecorationSet.empty;

    // Each arriving batch gets a new mix of short and long words. Derive it from
    // the text so selection changes and local edits don't make the widths flicker.
    let seed = position;
    for (const character of lastText.slice(-64)) seed = Math.imul(seed ^ character.charCodeAt(0), 16777619) >>> 0;
    const variant = seed;
    const widths = Array.from({ length: 4 }, () => {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        return 16 + seed % 73;
    });

    // The variant key prevents ProseMirror from reusing the previous word shapes.
    return DecorationSet.create(doc, [Decoration.widget(position, () => {
        const tail = document.createElement("span");
        tail.className = "summary-generation-tail";
        tail.contentEditable = "false";
        tail.setAttribute("aria-hidden", "true");
        for (const [index, width] of widths.entries()) {
            const word = document.createElement("span");
            word.className = "summary-generation-word";
            word.style.width = `${width}px`;
            word.style.animationDelay = `${index * 140}ms`;
            tail.append(word);
        }
        return tail;
    }, { key: `summary-generation-tail-${variant}`, side: 1, marks: [], ignoreSelection: true })]);
}

/** Inline view decorations never become document content, saved text or clipboard data. */
export const SummaryGeneration = Extension.create({
    name: "summaryGeneration",
    addProseMirrorPlugins() {
        return [new Plugin<GenerationState>({
            key: generationKey,
            state: {
                init: () => ({ active: false, decorations: DecorationSet.empty }),
                apply(transaction, previous) {
                    const active = transaction.getMeta(generationKey) as boolean | undefined;
                    if (active === false) return { active: false, decorations: DecorationSet.empty };
                    if (active === true) return { active: true, decorations: trailingWords(transaction.doc) };
                    if (!previous.active || !transaction.docChanged) return previous;
                    const streamed = transaction.getMeta(summaryStreamTransaction);
                    return { active: true, decorations: streamed ? trailingWords(transaction.doc)
                        : previous.decorations.map(transaction.mapping, transaction.doc) };
                },
            },
            props: { decorations: state => generationKey.getState(state)?.decorations },
        })];
    },
});

export function setSummaryGenerationStreaming(editor: Editor, active: boolean) {
    if (editor.isDestroyed || generationKey.getState(editor.state)?.active === active) return;
    editor.view.dispatch(editor.state.tr.setMeta(generationKey, active).setMeta("addToHistory", false));
}
